/**
 * Evidence readers shared by the Stage 2 stance rules. Pure; every reader
 * returns null rather than a default when the evidence is absent, so a rule
 * can never turn a missing input into a site fact.
 */
import type { ReportTypeId } from '../../config/reportTypes';
import type { PhasePowerComponentKey, ProjectIntent, SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { EpaAttainmentData, NaaqsDesignation } from '../../services/dataProviders/epaAttainment';
import { describeEpaDesignation, epaDesignationAsOf, nonattainmentClaimText, readEpaDesignation } from '../sed/epaDesignation';
import { normaliseStateToCode, sedStateOrigin } from '../inferSiteState';
import {
  effectivePhaseCapacity,
  effectivePower,
  effectivePowerMwRange,
} from '../projectIntent';

/** Newest report of a type (same ordering as the composite's selector). */
export function latestStage1Report(
  reports: readonly SiteRagReport[],
  type: ReportTypeId,
): SiteRagReport | null {
  const matches = reports.filter(report => (report.reportType ?? 'feasibility') === type);
  if (matches.length === 0) return null;
  return [...matches].sort((a, b) => Date.parse(b.createdAt ?? '') - Date.parse(a.createdAt ?? ''))[0] ?? null;
}

export interface CampusLoad {
  /** Conservative lower edge of the declared ultimate load, MW. */
  minMW: number;
  /** Upper edge; Infinity for an open band. */
  maxMW: number;
  /** "1,750 MW" or "500–1,000 MW" — the declared figure, unconverted. */
  label: string;
  /** The declared meaning of the MW figure, when stated. */
  basis: 'IT load' | 'facility load' | null;
}

function formatMw(value: number): string {
  return `${Math.round(value).toLocaleString('en-US')} MW`;
}

/**
 * The declared ultimate campus load: the final build phase's cumulative
 * capacity when phases exist, else the campus requirement. Constraint rules
 * use the LOWER edge so a constraint holds across the whole declared range.
 */
export function campusLoad(intent: ProjectIntent | null | undefined): CampusLoad | null {
  if (!intent) return null;
  const phases = [...(intent.phases ?? [])].sort((a, b) => a.sequence - b.sequence);
  const final = phases[phases.length - 1];
  const range = final
    ? effectivePhaseCapacity(final).range
    : effectivePowerMwRange(effectivePower(intent));
  if (!range || !(range.min > 0)) return null;
  const basis = intent.powerBasis === 'it-load' ? 'IT load' : intent.powerBasis === 'facility' ? 'facility load' : null;
  const label = range.max === range.min
    ? formatMw(range.min)
    : Number.isFinite(range.max)
      ? `${formatMw(range.min).replace(' MW', '')}–${formatMw(range.max)}`
      : `${formatMw(range.min)}+`;
  return { minMW: range.min, maxMW: range.max, label, basis };
}

export interface DatedPhase {
  phaseId: string;
  label: string;
  /** ISO date (YYYY-MM-DD). */
  target: string;
}

/** Phases with an exact target date, in sequence order (campus date when unphased). */
export function datedPhases(intent: ProjectIntent | null | undefined): DatedPhase[] {
  if (!intent) return [];
  const phases = [...(intent.phases ?? [])].sort((a, b) => a.sequence - b.sequence);
  if (phases.length === 0) {
    const date = intent.inServiceDate?.trim();
    return date ? [{ phaseId: 'campus', label: 'The campus', target: date }] : [];
  }
  return phases.flatMap(phase => {
    const date = phase.targetInServiceDate?.trim();
    return date ? [{ phaseId: phase.id, label: phase.label || `Phase ${phase.sequence}`, target: date }] : [];
  });
}

/** Add whole years to an ISO date; returns YYYY-MM-DD. */
export function addYears(isoDate: string, years: number): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return isoDate.slice(0, 10);
  date.setUTCFullYear(date.getUTCFullYear() + years);
  return date.toISOString().slice(0, 10);
}

/** Phases whose target falls strictly before `cutoff` (both ISO dates). */
export function phasesBefore(phases: readonly DatedPhase[], cutoff: string): DatedPhase[] {
  return phases.filter(phase => phase.target.slice(0, 10) < cutoff.slice(0, 10));
}

export function phaseList(phases: readonly DatedPhase[]): string {
  return phases.map(phase => `${phase.label} (${phase.target.slice(0, 10)})`).join(', ');
}

/**
 * Screening vocabulary → supply option. The screening narrative names
 * technologies in prose; only unambiguous phrases map. Anything else stays a
 * screening note and is never guessed into an option.
 */
export type PowerOptionId =
  | 'grid'
  | 'gas-cc'
  | 'gas-sc'
  | 'recip'
  | 'solar-bess'
  | 'wind-bess'
  | 'smr'
  | 'large-nuclear';

const POWER_ALIASES: ReadonlyArray<{ option: PowerOptionId; pattern: RegExp }> = [
  { option: 'gas-cc', pattern: /\bcombined[- ]cycle\b|\bNGCC\b|\bCCGT\b|\bHRSG\b/i },
  { option: 'gas-sc', pattern: /\bsimple[- ]cycle\b|\baero-?derivative|\bpeaker\b/i },
  { option: 'recip', pattern: /\breciprocating\b|\brecips?\b|\bgas engines?\b/i },
  { option: 'solar-bess', pattern: /\bsolar\b|\bphotovoltaic\b|\bPV\b/i },
  { option: 'wind-bess', pattern: /\bwind\b/i },
  { option: 'smr', pattern: /\bSMRs?\b|\bsmall modular reactors?\b|\bBWRX-300\b|\bmicroreactors?\b/i },
  { option: 'grid', pattern: /\bgrid\b|\butility (?:service|power|supply|import)\b/i },
];

/** Options named in a screening phrase (deduplicated, catalogue order). */
export function powerOptionsNamedIn(text: string | null | undefined): PowerOptionId[] {
  if (!text?.trim()) return [];
  return POWER_ALIASES.filter(alias => alias.pattern.test(text)).map(alias => alias.option);
}

export function mentionsBattery(text: string | null | undefined): boolean {
  return !!text && /\bBESS\b|\bbatter(?:y|ies)\b|\benergy storage\b/i.test(text);
}

/** Supply option → the phase component vocabulary the workbench seeds from. */
export const OPTION_COMPONENT: Readonly<Record<PowerOptionId, PhasePowerComponentKey>> = {
  grid: 'grid',
  'gas-cc': 'gas-turbine',
  'gas-sc': 'gas-turbine',
  recip: 'recip',
  'solar-bess': 'solar-bess',
  'wind-bess': 'wind-bess',
  smr: 'smr',
  'large-nuclear': 'large-nuclear',
};

/** "Phase 2" / "Phase II" → 2; null when the label carries no ordinal. */
export function phaseOrdinal(label: string | null | undefined): number | null {
  if (!label) return null;
  const arabic = /\bphase\s*(\d{1,2})\b/i.exec(label);
  if (arabic) return Number(arabic[1]);
  const roman = /\bphase\s*(i{1,3}|iv|v)\b/i.exec(label);
  if (!roman) return null;
  return ({ i: 1, ii: 2, iii: 3, iv: 4, v: 5 } as Record<string, number>)[roman[1]!.toLowerCase()] ?? null;
}

/**
 * Nonattainment reported by Stage 1 evidence: the EPA Green Book designation
 * persisted on the SED (`readEpaNonattainment`) or, when that was not
 * assessed, a positive statement in screening text (`readNonattainment`).
 * ABSENCE of evidence is never read as attainment.
 */
export interface NonattainmentSignal {
  /**
   * The pollutant the statement names. Ozone wins when both are named (its
   * NOx/VOC thresholds bind combustion first); PM-10 Serious follows ozone
   * and PM2.5 for coverage-backed EPA listings. `unspecified` when the text
   * names none — the reason then asks for the designation instead of
   * assuming ozone.
   */
  pollutant: 'ozone' | 'pm2.5' | 'pm10' | 'unspecified';
  classification: 'marginal' | 'moderate' | 'serious' | 'severe' | 'extreme' | null;
  text: string;
  evidencePath: string;
  /** Present when the signal comes from the EPA Green Book designation. */
  epa?: {
    /** EPA export date of the table, e.g. '2026-08-31'. */
    asOf: string;
    /**
     * Every current area carrying the reported classification covers only
     * part of the county (the stated NNSR threshold may not apply at the site).
     */
    partOfCounty: boolean;
    /**
     * When `partOfCounty`: a less stringent area for the same pollutant that
     * covers the whole county, e.g. Weld County, CO (08123) — 2008 ozone
     * Severe 15 in part of the county, 2015 ozone Serious across all of it.
     */
    wholeCountyArea?: { classification: NonattainmentSignal['classification'] };
    /**
     * EPA-7 — present exactly when `partOfCounty`: whether EPA's
     * nonattainment-area boundaries place the site point inside an area
     * carrying `classification` ('inside' when any does; 'unknown' when not
     * resolved, including SEDs built before EPA-7). 'outside' means the point
     * lies outside EVERY current area of the pollutant; `classification` is
     * then null and `outsideAreas` names the classes.
     */
    siteContainment?: 'inside' | 'outside' | 'unknown';
    /**
     * EPA-7 — part-county areas of this pollutant EPA's boundaries place the
     * site point outside, most stringent first. When present and
     * `siteContainment` is not 'outside', `classification` is the most
     * stringent class that can still apply at the point (Weld County outside
     * the Severe 15 part: serious, from the whole-county area).
     */
    outsideAreas?: Array<{ classification: NonattainmentSignal['classification'] }>;
  };
}

const CLASSIFICATION = /\b(marginal|moderate|serious|severe|extreme)\b/i;
/** A class the text negates ("classified Marginal, not Severe") is never a candidate. */
const NEGATED_CLASSIFICATION = /\b(?:not|never|no\s+longer|n['’]t)\s+(?:(?:a|an|the|yet|currently|been)\s+)?(?:marginal|moderate|serious|severe|extreme)\b/gi;
const OZONE = /\bozone\b|\bO3\b|\b8[- ]hour\b/i;
const PM25 = /\bPM\s?2\.5\b|\bfine particulate/i;

export function readNonattainment(
  sources: ReadonlyArray<{ text: string | null | undefined; evidencePath: string }>,
): NonattainmentSignal | null {
  for (const source of sources) {
    const text = source.text?.trim();
    // Shared per-mention reader: a negated or revoked-standard mention cannot
    // mask a current one, and pollutant and class come from the claim itself.
    const claim = nonattainmentClaimText(text);
    if (!text || !claim) continue;
    const pollutant = OZONE.test(claim) ? 'ozone' : PM25.test(claim) ? 'pm2.5' : 'unspecified';
    // The first class the claim names, as before, skipping a class the text
    // negates ("classified Marginal, not Severe"). Not the most stringent: a
    // neighbour's stricter class ("…; the nearest Severe area is 60 miles
    // east") must not set the site's threshold.
    const match = pollutant === 'unspecified' ? null : CLASSIFICATION.exec(claim.replace(NEGATED_CLASSIFICATION, ' '));
    return {
      pollutant,
      classification: ruleClassification(match?.[1]),
      text,
      evidencePath: source.evidencePath,
    };
  }
  return null;
}

export const EPA_DESIGNATION_EVIDENCE_PATH = 'sed.environmental.naaqsDesignations';
const EIGHT_HOUR_OZONE = /^8-Hour Ozone\b/;

/** Pollutants with a CURRENT nonattainment designation in a coverage-backed listing. */
function epaNonattainmentPollutants(epa: EpaAttainmentData): Map<'ozone' | 'pm2.5' | 'pm10' | 'other', NaaqsDesignation[]> {
  const byPollutant = new Map<'ozone' | 'pm2.5' | 'pm10' | 'other', NaaqsDesignation[]>();
  for (const d of epa.designations ?? []) {
    if (d.status !== 'nonattainment') continue;
    const key = d.pollutant === 'ozone' && EIGHT_HOUR_OZONE.test(d.naaqs) ? 'ozone' : d.pollutant === 'pm2.5' ? 'pm2.5' : d.pollutant === 'pm10' ? 'pm10' : 'other';
    byPollutant.set(key, [...(byPollutant.get(key) ?? []), d]);
  }
  return byPollutant;
}

/**
 * Nonattainment from the EPA Green Book designation on the SED. Tri-state:
 * `undefined` when not assessed (no block, or no national coverage), so the
 * caller falls back to screening text; `null` when the coverage-backed
 * listing has no current 8-hour ozone, PM2.5 or Serious PM-10 nonattainment (EPA overrides
 * stale text); otherwise a signal, ozone first (its NOx/VOC thresholds bind
 * combustion first), then PM2.5, then PM-10 Serious. Other listings remain
 * visible through the informational other-standards reader.
 */
export function readEpaNonattainment(epa: EpaAttainmentData | null | undefined): NonattainmentSignal | null | undefined {
  const asOf = epaDesignationAsOf(epa);
  if (!epa || !asOf || readEpaDesignation(epa) === 'not-assessed') return undefined;
  const current = epaNonattainmentPollutants(epa);
  const ozone = current.get('ozone');
  const pm25 = current.get('pm2.5');
  const pm10 = current.get('pm10');
  // Owner D03 (30 September 2026): only PM-10 Serious adds a combustion
  // constraint. Moderate-only remains in the informational other standards.
  // Precedence and county screening do not follow point containment.
  const seriousPm10 = pm10?.some(d => ruleClassification(d.classification) === 'serious');
  const pollutant = ozone ? 'ozone' as const : pm25 ? 'pm2.5' as const : seriousPm10 ? 'pm10' as const : null;
  if (!pollutant) return null;
  const areas = (ozone ?? pm25 ?? pm10)!;
  const particulateClassName = (rows: readonly NaaqsDesignation[]): string | undefined => (
    rows.some(d => /serious/i.test(d.classification ?? '')) ? 'Serious' : rows.find(d => d.classification)?.classification);
  let classification = ruleClassification(pollutant === 'ozone' ? epa.classification : particulateClassName(areas));
  // EPA-7: a class whose every area EPA's boundaries place the site point
  // outside cannot apply there; the next class down is read from the areas
  // left. Areas of one standard do not overlap, and a whole-county area
  // always contains the point, so the loop ends at a whole-county area, an
  // area containing (or possibly containing) the point, or none.
  let remaining: readonly NaaqsDesignation[] = areas;
  const outsideAreas: Array<{ classification: NonattainmentSignal['classification'] }> = [];
  for (;;) {
    // The part-county fact follows the areas behind the stated threshold:
    // every ozone class has its own, PM2.5/PM-10 separate serious (70 tpy)
    // from 100.
    const carrying = classification && (pollutant === 'ozone' || classification === 'serious')
      ? remaining.filter(d => ruleClassification(d.classification) === classification)
      : remaining;
    const binding = carrying.length > 0 ? carrying : remaining;
    const partOfCounty = binding.every(d => d.extent === 'part');
    const containment = partOfCounty ? siteContainmentOf(binding) : 'inside';
    if (containment === 'outside') {
      outsideAreas.push({ classification });
      remaining = remaining.filter(d => !binding.includes(d));
      if (remaining.length > 0) {
        classification = pollutant === 'ozone' ? mostStringentClassification(remaining) : ruleClassification(particulateClassName(remaining));
        continue;
      }
      return {
        pollutant,
        classification: null,
        text: describeEpaDesignation(epa),
        evidencePath: EPA_DESIGNATION_EVIDENCE_PATH,
        epa: { asOf, partOfCounty: true, siteContainment: 'outside', outsideAreas },
      };
    }
    const wholeCounty = partOfCounty ? remaining.filter(d => d.extent === 'whole') : [];
    return {
      pollutant,
      classification,
      text: describeEpaDesignation(epa),
      evidencePath: EPA_DESIGNATION_EVIDENCE_PATH,
      epa: {
        asOf,
        partOfCounty,
        ...(wholeCounty.length > 0 ? { wholeCountyArea: { classification: mostStringentClassification(wholeCounty) } } : {}),
        ...(partOfCounty ? { siteContainment: containment } : {}),
        ...(outsideAreas.length > 0 ? { outsideAreas } : {}),
      },
    };
  }
}

/**
 * EPA-7 — site-point containment across areas: 'inside' when EPA's boundary
 * places the point in any of them (a whole-county area always contains it),
 * 'outside' when every one excludes it, else 'unknown'. A part-county row
 * without a result (a failed lookup, or an SED from before EPA-7) is unknown.
 */
export function siteContainmentOf(areas: readonly NaaqsDesignation[]): 'inside' | 'outside' | 'unknown' {
  const statuses = areas.map(d => (d.extent === 'whole' ? 'inside' : d.siteContainment?.status ?? 'unknown'));
  if (statuses.includes('inside')) return 'inside';
  return statuses.length > 0 && statuses.every(status => status === 'outside') ? 'outside' : 'unknown';
}

const RULE_CLASSIFICATION_ORDER = ['marginal', 'moderate', 'serious', 'severe', 'extreme'] as const;

function ruleClassification(className: string | undefined): NonattainmentSignal['classification'] {
  const match = className ? CLASSIFICATION.exec(className) : null;
  return match ? match[1]!.toLowerCase() as NonattainmentSignal['classification'] : null;
}

function mostStringentOf(classNames: Iterable<string | undefined>): NonattainmentSignal['classification'] {
  let most: NonattainmentSignal['classification'] = null;
  for (const name of classNames) {
    const c = ruleClassification(name);
    if (c && (!most || RULE_CLASSIFICATION_ORDER.indexOf(c) > RULE_CLASSIFICATION_ORDER.indexOf(most))) most = c;
  }
  return most;
}

function mostStringentClassification(areas: readonly NaaqsDesignation[]): NonattainmentSignal['classification'] {
  return mostStringentOf(areas.map(d => d.classification));
}

/**
 * The screening-text signal a coverage-backed EPA listing contradicts (the
 * listing has no current nonattainment for the pollutant the text names, or
 * none at all when the text names no pollutant), or null. Callers surface it
 * rather than silently dropping it.
 */
export function epaContradictedText(
  epa: EpaAttainmentData | null | undefined,
  text: NonattainmentSignal | null,
): NonattainmentSignal | null {
  if (!text || text.epa || readEpaNonattainment(epa) === undefined) return null;
  const current = epaNonattainmentPollutants(epa!);
  const listed = text.pollutant === 'unspecified' ? current.size > 0 : current.has(text.pollutant);
  return listed ? null : text;
}

/** A current nonattainment standard the air rule's constrained reason does not cover. */
export interface EpaOtherNonattainment {
  /** EPA NAAQS name, e.g. "Sulfur Dioxide (2010)", "PM-10 (1987)". */
  naaqs: string;
  /** Distinct EPA classifications across the county's areas for this standard. */
  classifications: string[];
  /** Every area for this standard covers only part of the county. */
  partOfCounty: boolean;
  /** EPA-7 — present exactly when `partOfCounty`: site-point containment across the areas (`siteContainmentOf`). */
  siteContainment?: 'inside' | 'outside' | 'unknown';
}

/**
 * Current nonattainment standards a coverage-backed EPA listing carries beyond
 * the one the air signal covers (`covered`: the EPA signal's pollutant, or
 * null when there is none). SO2, PM-10, lead and CO — and PM2.5 when ozone
 * took the signal — would otherwise be silent: at gigawatt scale a gas
 * campus's potential SO2 or PM-10 can reach the 70–100 tpy nonattainment
 * major-source thresholds, so they are reported. PM-10 Serious constrains
 * combustion under owner D03; Moderate and other secondary standards stay
 * informational. This reader does not estimate potential to emit.
 */
export function readEpaOtherNonattainment(
  epa: EpaAttainmentData | null | undefined,
  covered: NonattainmentSignal['pollutant'] | null,
): EpaOtherNonattainment[] {
  if (readEpaNonattainment(epa) === undefined) return [];
  const byStandard = new Map<string, NaaqsDesignation[]>();
  for (const d of epa!.designations ?? []) {
    if (d.status !== 'nonattainment') continue;
    const pollutant = d.pollutant === 'ozone' && EIGHT_HOUR_OZONE.test(d.naaqs) ? 'ozone' : d.pollutant;
    if (pollutant === covered) continue;
    byStandard.set(d.naaqs, [...(byStandard.get(d.naaqs) ?? []), d]);
  }
  return [...byStandard].map(([naaqs, areas]) => {
    const partOfCounty = areas.every(d => d.extent === 'part');
    return {
      naaqs,
      classifications: [...new Set(areas.flatMap(d => (d.classification ? [d.classification] : [])))],
      partOfCounty,
      ...(partOfCounty ? { siteContainment: siteContainmentOf(areas) } : {}),
    };
  });
}

/**
 * The Ozone Transport Region (Clean Air Act §184(a)): eleven whole states and
 * the Consolidated Metropolitan Statistical Area that includes the District of
 * Columbia. That area adds the District itself (Maryland is already whole)
 * and ten Virginia localities, which EPA lists by name (83 FR 45351,
 * 7 September 2018; 9VAC5-80-2010 lists the same ten). Keys are USPS codes and
 * Census 2020 county-equivalent FIPS. Reviewed 28 September 2026.
 */
const OTR_WHOLE_JURISDICTIONS: Readonly<Record<string, string>> = {
  CT: 'Connecticut', DE: 'Delaware', DC: 'The District of Columbia', ME: 'Maine',
  MD: 'Maryland', MA: 'Massachusetts', NH: 'New Hampshire', NJ: 'New Jersey',
  NY: 'New York', PA: 'Pennsylvania', RI: 'Rhode Island', VT: 'Vermont',
};
const OTR_VIRGINIA_LOCALITIES: Readonly<Record<string, string>> = {
  '51013': 'Arlington County', '51059': 'Fairfax County', '51107': 'Loudoun County',
  '51153': 'Prince William County', '51179': 'Stafford County', '51510': 'Alexandria City',
  '51600': 'Fairfax City', '51610': 'Falls Church City', '51683': 'Manassas City',
  '51685': 'Manassas Park City',
};

export type OtrMembership =
  | { status: 'not-assessed' }
  | { status: 'outside'; state: string; evidencePath: string }
  | {
    status: 'inside';
    state: string;
    /** "Pennsylvania", "The District of Columbia", or a Virginia locality, e.g. "Loudoun County". */
    place: string;
    /** True when EPA's Virginia locality list, not a whole-state membership, placed the site. */
    virginiaLocality: boolean;
    evidencePath: string;
  };

/**
 * The site's state, and its county FIPS when known. The EPA listing's state
 * and county come from one point-in-county lookup (Census TIGER, else FCC),
 * so they are read together and win. Otherwise the EIA electricity state,
 * resolved the same way, places the site on its own — but only when the EIA
 * provider wrote it: a state a document, research, DAM or a prior report
 * supplied is not a coordinate lookup (DAT-FOLLOWUP-4, `sedStateOrigin`).
 *
 * The regulatory state never places a site. regulatory-db falls back to a
 * coordinate bounding box, too coarse at the region's borders (PA–OH, PA–WV,
 * MD–WV), and marks that estimate (`stateResolution: 'bbox-estimate'`) only
 * on SEDs compiled since DAT-FOLLOWUP-4; an unmarked regulatory state may be
 * a legacy estimate, so it cannot prove a lookup. It vetoes an electricity
 * state it contradicts (either can carry a document-extracted state,
 * DAT-FOLLOWUP-1) unless it is a marked estimate: a bounding-box guess does
 * not overrule a TIGER/FCC lookup.
 */
function otrLocation(
  sed: SiteEvidenceDocument | null | undefined,
): { state: string; countyFips: string | null; evidencePath: string } | null {
  const epa = sed?.environmental?.data?.naaqsDesignations;
  const epaState = normaliseStateToCode(epa?.state);
  if (epaState) {
    const countyFips = epa?.fips && /^\d{5}$/.test(epa.fips) ? epa.fips : null;
    return { state: epaState, countyFips, evidencePath: EPA_DESIGNATION_EVIDENCE_PATH };
  }
  const electricity = normaliseStateToCode(sed?.electricity?.data?.state);
  if (!electricity || sedStateOrigin(sed?.electricity) !== 'provider') return null;
  const regulatory = normaliseStateToCode(sed?.regulatory?.data?.state);
  const regulatoryVetoes = regulatory !== null && regulatory !== electricity
    && sedStateOrigin(sed?.regulatory) !== 'estimate';
  if (regulatoryVetoes) return null;
  return { state: electricity, countyFips: null, evidencePath: 'sed.electricity.state' };
}

/**
 * Ozone Transport Region membership from the site's state and, for Virginia,
 * its county. Only part of Virginia is in the region, so a Virginia site
 * without its county is not assessed; so is a site with no recognised state.
 */
export function readOtrMembership(sed: SiteEvidenceDocument | null | undefined): OtrMembership {
  const location = otrLocation(sed);
  if (!location) return { status: 'not-assessed' };
  const { state, countyFips, evidencePath } = location;
  const whole = OTR_WHOLE_JURISDICTIONS[state];
  if (whole) return { status: 'inside', state, place: whole, virginiaLocality: false, evidencePath };
  if (state !== 'VA') return { status: 'outside', state, evidencePath };
  if (!countyFips?.startsWith('51')) return { status: 'not-assessed' };
  const locality = OTR_VIRGINIA_LOCALITIES[countyFips];
  return locality
    ? { status: 'inside', state, place: locality, virginiaLocality: true, evidencePath }
    : { status: 'outside', state, evidencePath };
}
